import { useState, useEffect, useCallback } from "react";
import {
  _get_support_dashboard_metrics,
  _get_support_businesses,
  _get_support_orders_new,
  _get_support_orders,
  _get_delayed_deliveries,
  _get_support_dashboard_home,
  _get_delivery_details_new,
  _reassign_delivery_biker,
  _cancel_delayed_delivery,
  _contact_delivery_biker,
  _contact_delivery_customer,
  _escalate_delivery,
  _update_delivery_eta,
  _get_delay_performance_analytics,
  _get_available_bikers,
  _cancel_order_by_sm,
  _get_interventions_list,
} from "../../../common/axios_services";

const useSupportData = () => {
  const [metrics, setMetrics] = useState({});
  const [businesses, setBusinesses] = useState([]);
  const [businessPagination, setBusinessPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [orders, setOrders] = useState([]);
  const [delayedDeliveries, setDelayedDeliveries] = useState([]);
  const [delayAnalytics, setDelayAnalytics] = useState({});
  const [dashboardStats, setDashboardStats] = useState({
    businessCount: 0,
    todayNGN: 0,
    todayOrderCount: 0,
    salesTillDate: 0,
    interventionCount: 0,
  });
  const [slaViolations, setSlaViolations] = useState({
    notAccept: [],
    notReady: [],
    scheduled: [],
    notPicked: [],
    notDelivered: [],
  });
  const [driverSlaViolations, setDriverSlaViolations] = useState({
    waitingToAccept: [],
    waitingToPickUp: [],
    waitingToStart: [],
    waitingToArrive: [],
  });
  const [ordersByTab, setOrdersByTab] = useState({
    sales: { orders: [], pagination: {} },
    todays: { orders: [], pagination: {} },
    scheduled: { orders: [], pagination: {} },
    completed: { orders: [], pagination: {} },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [businessFilters, setBusinessFilters] = useState({});

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await _get_support_dashboard_home();
      setDashboardStats(response.data?.data);
      return { success: true };
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err.message ||
        "Failed to load dashboard";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const calculateMetrics = useCallback((ordersData, restaurantsData) => {
    // Defensive: ensure ordersData is an array
    const orders = Array.isArray(ordersData) ? ordersData : [];
    const restaurants = Array.isArray(restaurantsData) ? restaurantsData : [];
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const pendingOrders =
      orders.filter((order) =>
        ["WAITING", "ACCEPTED", "READY", "IN_TRANSIT"].includes(order.status),
      ).length || 0;

    const deliveredOrders =
      orders.filter((order) => {
        if (order.deliveryType === "PICK-UP") {
          return order.status?.includes("PICKED");
        }
        return order.status === "DELIVERED";
      }).length || 0;

    const openRestaurants =
      restaurants.filter((restaurant) => restaurant.isOpen).length || 0;

    const todaySales =
      orders
        ?.filter((order) => {
          const orderDate = new Date(order.createdAt);
          return orderDate >= today && order.status === "DELIVERED";
        })
        .reduce((sum, order) => sum + (order.totalAmount || 0), 0) || 0;

    const totalSales =
      orders
        ?.filter((order) => order.status === "DELIVERED")
        .reduce((sum, order) => sum + (order.totalAmount || 0), 0) || 0;

    const activeDeliveries =
      orders?.filter((order) =>
        ["ASSIGNED", "PICKED", "IN_TRANSIT"].includes(order.delivery?.status),
      ).length || 0;

    const completedToday =
      orders?.filter((order) => {
        const orderDate = new Date(order.updatedAt || order.createdAt);
        return orderDate >= today && order.status === "DELIVERED";
      }).length || 0;

    const delayed =
      orders?.filter((order) => {
        if (!order.delivery?.assignedAt) return false;
        const assignedTime = new Date(order.delivery.assignedAt);
        const hoursDiff = (now - assignedTime) / (1000 * 60 * 60);
        return hoursDiff > 2 && !["DELIVERED", "PICKED"].includes(order.status);
      }).length || 0;

    return {
      totalRestaurants: restaurants?.length || 0,
      openRestaurants,
      pendingOrders,
      deliveredOrders,
      todaySales,
      totalSales,
      delayedDeliveries: delayed,
      totalOrders: orders?.length || 0,
      activeDeliveries,
      completedToday,
    };
  }, []);

  const fetchDashboardData = useCallback(
    async (filters = {}) => {
      try {
        setLoading(true);
        setError(null);

        const [businessesRes, ordersRes, delayedRes, analyticsRes] =
          await Promise.all([
            _get_support_businesses({
              ...filters,
              page: filters.page || 1,
              limit: filters.limit || 20,
            }),
            _get_support_orders_new(),
            _get_delayed_deliveries({ page: 1, limit: 20 }),
            _get_delay_performance_analytics(),
          ]);

        const businessesData =
          businessesRes?.data?.data?.data || businessesRes?.data?.data || [];
        const paginationData = businessesRes?.data?.data?.pagination || {};
        const ordersData =
          ordersRes?.data?.data?.data || ordersRes?.data?.data || [];
        const delayedData = delayedRes?.data?.deliveries || [];
        const analyticsData = analyticsRes?.data || {};

        setBusinesses(businessesData);
        setBusinessPagination({
          page: paginationData.page || 1,
          limit: paginationData.limit || 20,
          total: paginationData.total || 0,
          totalPages: paginationData.totalPages || 0,
        });
        setOrders(ordersData);
        setDelayedDeliveries(delayedData);
        setDelayAnalytics(analyticsData);
        setBusinessFilters(filters);

        const calculatedMetrics = calculateMetrics(ordersData, businessesData);
        setMetrics(calculatedMetrics);

        return { success: true };
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        const errorMessage =
          err?.response?.data?.message ||
          err.message ||
          "Failed to load dashboard data";
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [calculateMetrics],
  );

  const fetchSlaViolations = useCallback(
    async (
      page = 1,
      limit = 20,
      supportManagerId = null,
      supportOfficerId = null,
    ) => {
      try {
        setLoading(true);
        const [notAcceptRes, notReadyRes, scheduledRes, notPickedRes, notDeliveredRes] =
          await Promise.all([
            _get_interventions_list(
              "not-accept",
              page,
              limit,
              supportManagerId,
              supportOfficerId,
            ),
            _get_interventions_list(
              "scheduled",
              page,
              limit,
              supportManagerId,
              supportOfficerId,
            ),
            _get_interventions_list(
              "not-ready",
              page,
              limit,
              supportManagerId,
              supportOfficerId,
            ),
            _get_interventions_list(
              "not-picked",
              page,
              limit,
              supportManagerId,
              supportOfficerId,
            ),
            _get_interventions_list(
              "not-delivered",
              page,
              limit,
              supportManagerId,
              supportOfficerId,
            ),
          ]);

        setSlaViolations({
          notAccept: notAcceptRes?.data?.data?.orders || [],
          notReady: notReadyRes?.data?.data?.orders || [],
          scheduled: scheduledRes?.data?.data?.orders || [],
          notPicked: notPickedRes?.data?.data?.orders || [],
          notDelivered: notDeliveredRes?.data?.data?.orders || [],
        });

        return { success: true };
      } catch (err) {
        console.error("Error fetching SLA violations:", err);
        setError(err.message || "Failed to fetch SLA violations");
        return { success: false, error: err.message };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const fetchDriverSlaViolations = useCallback(
    async (
      page = 1,
      limit = 20,
      supportManagerId = null,
      supportOfficerId = null,
    ) => {
      try {
        setLoading(true);
        const [waitPickUpRes, waitStartRes, waitArriveRes] =
          await Promise.all([
            _get_interventions_list(
              "waiting-to-pick-up",
              page,
              limit,
              supportManagerId,
              supportOfficerId,
            ),
            _get_interventions_list(
              "waiting-to-start",
              page,
              limit,
              supportManagerId,
              supportOfficerId,
            ),
            _get_interventions_list(
              "waiting-to-arrive",
              page,
              limit,
              supportManagerId,
              supportOfficerId,
            ),
          ]);

        setDriverSlaViolations({
          waitingToAccept: [],
          waitingToPickUp: waitPickUpRes?.data?.data?.orders || [],
          waitingToStart: waitStartRes?.data?.data?.orders || [],
          waitingToArrive: waitArriveRes?.data?.data?.orders || [],
        });

        return { success: true };
      } catch (err) {
        setError(err.message || "Failed to fetch driver SLA violations");
        return { success: false, error: err.message };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const fetchOrdersByTab = useCallback(
    async (tab = "sales", page = 1, limit = 20) => {
      try {
        setLoading(true);
        const response = await _get_support_orders(tab, page, limit);
        setOrdersByTab((prev) => ({
          ...prev,
          [tab]: {
            orders: response?.data?.data?.orders || [],
            pagination: response?.data?.data?.pagination || {},
          },
        }));
        return { success: true };
      } catch (err) {
        console.error(`Error fetching ${tab} orders:`, err);
        setError(err.message || `Failed to fetch ${tab} orders`);
        return { success: false, error: err.message };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const fetchBusinessesWithPagination = useCallback(
    async (page = 1, limit = 20, filters = {}) => {
      try {
        setLoading(true);
        const response = await _get_support_businesses({
          ...filters,
          page,
          limit,
        });

        const businessesData =
          response?.data?.data?.data || response?.data?.data || [];
        const paginationData = response?.data?.data?.pagination || {};

        setBusinesses(businessesData);
        setBusinessPagination({
          page: paginationData.page || page,
          limit: paginationData.limit || limit,
          total: paginationData.total || 0,
          totalPages: paginationData.totalPages || 0,
        });
        setBusinessFilters(filters);

        return { success: true };
      } catch (err) {
        console.error("Error fetching businesses:", err);
        const errorMessage =
          err?.response?.data?.message ||
          err.message ||
          "Failed to load businesses";
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const refreshData = useCallback(() => {
    return fetchDashboardData();
  }, [fetchDashboardData]);

  const viewOrderDetails = useCallback(async (orderId) => {
    try {
      const response = await _get_delivery_details_new(orderId);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error.message ||
          "Failed to load order details",
      };
    }
  }, []);

  const cancelOrder = useCallback(
    async (orderId, reason) => {
      try {
        await _cancel_order_by_sm(orderId, reason);
        await refreshData();
        return { success: true, message: "Order rejected successfully" };
      } catch (error) {
        return {
          success: false,
          error:
            error?.response?.data?.message ||
            error.message ||
            "Failed to cancel order",
        };
      }
    },
    [refreshData],
  );

  const reassignBiker = useCallback(
    async (deliveryId, newBikerId, reason) => {
      try {
        await _reassign_delivery_biker(deliveryId, { newBikerId, reason });
        await refreshData();
        return { success: true, message: "Biker reassigned successfully" };
      } catch (error) {
        return {
          success: false,
          error: error.message || "Failed to reassign biker",
        };
      }
    },
    [refreshData],
  );

  const cancelDelayedDelivery = useCallback(
    async (deliveryId, reason, refundAmount) => {
      try {
        await _cancel_delayed_delivery(deliveryId, { reason, refundAmount });
        await refreshData();
        return { success: true, message: "Delivery cancelled with refund" };
      } catch (error) {
        return {
          success: false,
          error: error.message || "Failed to cancel delivery",
        };
      }
    },
    [refreshData],
  );

  const contactDeliveryBiker = useCallback(
    async (deliveryId, message, priority = "normal") => {
      try {
        await _contact_delivery_biker(deliveryId, { message, priority });
        return { success: true, message: "Message sent to biker" };
      } catch (error) {
        return {
          success: false,
          error: error.message || "Failed to contact biker",
        };
      }
    },
    [],
  );

  const contactDeliveryCustomer = useCallback(async (deliveryId, message) => {
    try {
      await _contact_delivery_customer(deliveryId, { message });
      return { success: true, message: "Message sent to customer" };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to contact customer",
      };
    }
  }, []);

  const escalateDelivery = useCallback(
    async (deliveryId, issueType, description, severity) => {
      try {
        await _escalate_delivery(deliveryId, {
          issueType,
          description,
          severity,
        });
        await refreshData();
        return { success: true, message: "Delivery escalated to management" };
      } catch (error) {
        return {
          success: false,
          error: error.message || "Failed to escalate delivery",
        };
      }
    },
    [refreshData],
  );

  const updateDeliveryEta = useCallback(
    async (deliveryId, newEta, reason) => {
      try {
        await _update_delivery_eta(deliveryId, { newEta, reason });
        await refreshData();
        return { success: true, message: "Delivery ETA updated" };
      } catch (error) {
        return {
          success: false,
          error: error.message || "Failed to update ETA",
        };
      }
    },
    [refreshData],
  );

  const getAvailableBikers = useCallback(async () => {
    try {
      const response = await _get_available_bikers({ page: 1, pageSize: 50 });
      return { success: true, data: response.data || [] };
    } catch (error) {
      console.error("Error fetching available bikers:", error);
      return { success: false, data: [], error: error.message };
    }
  }, []);

  const performBulkAction = useCallback(async (actionData) => {
    console.log("Bulk action:", actionData);
    return {
      success: false,
      message: "Bulk operations feature coming soon",
    };
  }, []);

  const exportData = useCallback(async (exportData) => {
    console.log("Export data:", exportData);
    return { success: false, message: "Export feature coming soon" };
  }, []);

  const performSystemCheck = useCallback(async () => {
    return {
      success: true,
      healthy: true,
      checks: [
        {
          name: "API Services",
          status: "healthy",
          message: "All APIs responding normally",
        },
        {
          name: "Database",
          status: "healthy",
          message: "Database connections stable",
        },
        {
          name: "Delivery System",
          status: "healthy",
          message: "Delivery tracking operational",
        },
        {
          name: "Payment Gateway",
          status: "healthy",
          message: "Payment processing active",
        },
      ],
    };
  }, []);

  useEffect(() => {
    fetchDashboardData();
    fetchSlaViolations(1, 20);
    fetchDriverSlaViolations(1, 20);
  }, [fetchDashboardData, fetchSlaViolations, fetchDriverSlaViolations]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return {
    dashboardStats,
    metrics,
    businesses,
    businessPagination,
    orders,
    delayedDeliveries,
    delayAnalytics,
    slaViolations,
    driverSlaViolations,
    ordersByTab,
    loading,
    error,
    businessFilters,

    refreshData,
    fetchSlaViolations,
    fetchDriverSlaViolations,
    fetchOrdersByTab,
    fetchBusinessesWithPagination,
    viewOrderDetails,
    cancelOrder,
    reassignBiker,
    cancelDelayedDelivery,
    contactDeliveryBiker,
    contactDeliveryCustomer,
    escalateDelivery,
    updateDeliveryEta,
    getAvailableBikers,
    performBulkAction,
    exportData,
    performSystemCheck,
    fetchDashboardData,
  };
};

export default useSupportData;
